/* eslint-disable react-refresh/only-export-components -- module exports toast helpers alongside small presentational components */
import {
  CheckCircle,
  AlertTriangle,
  Info,
  AlertCircle,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Shared layout helper — keeps toast markup DRY while allowing per-type icons
// ---------------------------------------------------------------------------

type ToastShellProps = {
  id: string | number;
  colorClass: string;   // border + bg tint token classes
  iconBgClass: string;  // icon circle background
  icon: React.ReactNode;
  title: React.ReactNode;
  titleClassName?: string;
  body?: React.ReactNode;
  bodyClassName?: string;
  footer?: React.ReactNode;
};

const ToastShell = ({
  id,
  colorClass,
  iconBgClass,
  icon,
  title,
  titleClassName,
  body,
  bodyClassName,
  footer,
}: ToastShellProps) => {
  return (
    <div
      className={cn(
        "flex w-full max-w-md items-start gap-3 rounded-xl border p-4 shadow-2xl bg-card/95 backdrop-blur-xs relative",
        "animate-in slide-in-from-top-2 fade-in duration-300",
        colorClass
      )}
    >
      <div className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full", iconBgClass)}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className={cn("text-sm font-medium text-foreground select-none", titleClassName)}>{title}</div>
          <button
            onClick={() => toast.dismiss(id)}
            className="ml-4 rounded-full p-1 text-muted-foreground opacity-70 transition-opacity hover:bg-muted hover:opacity-100 absolute top-1 right-1"
          >
            <X className="size-4 select-none cursor-pointer" />
          </button>
        </div>
        {body && (
          <div className={cn("mt-1 text-xs text-muted-foreground select-none", bodyClassName)}>
            {body}
          </div>
        )}
        {footer && <div className="mt-3">{footer}</div>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Error toasts — destructive semantic color
// ---------------------------------------------------------------------------

export function showErrorToastWithAction(
  message: string,
  retryFunction: () => void
) {
  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-destructive/60 ring-1 ring-destructive/30"
        iconBgClass="bg-destructive/15"
        icon={<AlertCircle className="size-4 text-destructive select-none" />}
        titleClassName="text-destructive"
        title={message}
        bodyClassName="text-foreground/90"
        body="Der opstod en fejl under behandlingen af din anmodning. Prøv venligst igen."
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => { toast.dismiss(id); retryFunction(); }}
              className="inline-flex items-center justify-center rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
            >
              Prøv igen
            </button>
            <button
              onClick={() => toast.dismiss(id)}
              className="inline-flex items-center justify-center rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
            >
              Luk
            </button>
          </div>
        }
      />
    ),
    { duration: 5000, position: "bottom-right" }
  );
}

export function showErrorToast(message: string, title: string = "Fejl!") {
  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-destructive/60 ring-1 ring-destructive/30"
        iconBgClass="bg-destructive/15"
        icon={<AlertCircle className="size-4 text-destructive select-none" />}
        titleClassName="text-destructive"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
      />
    ),
    { duration: 5000, position: "bottom-right" }
  );
}

// ---------------------------------------------------------------------------
// Success toast — success semantic color
// ---------------------------------------------------------------------------

export function showSuccessToast(message: string, title: string = "Succes!") {
  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-success/60 ring-1 ring-success/30"
        iconBgClass="bg-success/20"
        icon={<CheckCircle className="size-4 text-success select-none" />}
        titleClassName="text-success"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
      />
    ),
    { duration: 5000, position: "bottom-right" }
  );
}

// ---------------------------------------------------------------------------
// Warning toast — warning semantic color
// ---------------------------------------------------------------------------

export function showWarningToast(
  message: string,
  title: string = "Advarsel!",
  timeout: number = 5000
) {
  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-warning/60 ring-1 ring-warning/30"
        iconBgClass="bg-warning/20"
        icon={<AlertTriangle className="size-4 text-warning select-none" />}
        titleClassName="text-warning"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
      />
    ),
    { duration: timeout, position: "bottom-right" }
  );
}

// ---------------------------------------------------------------------------
// Info / Loading / Progress toasts — primary brand color
// ---------------------------------------------------------------------------

export function showInfoToast(
  message: string,
  title: string = "Info!",
  timeout: number = 5000
) {
  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-primary/55 ring-1 ring-primary/30"
        iconBgClass="bg-primary/20"
        icon={<Info className="size-4 text-primary select-none" />}
        titleClassName="text-primary"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
      />
    ),
    { duration: timeout, position: "bottom-right" }
  );
}

/**
 * An info toast with a single action, which stays until the user dismisses it.
 *
 * For things the user has to come back to rather than read in passing — a
 * SharePoint download that finished after they navigated away, for instance,
 * where the action takes them to the workspace the upload is waiting on.
 * Returns the toast id so the caller can dismiss it once the work resumes.
 */
export function showInfoToastWithAction(
  message: string,
  title: string,
  actionLabel: string,
  onAction: () => void
): string | number {
  return toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-primary/55 ring-1 ring-primary/30"
        iconBgClass="bg-primary/20"
        icon={<Info className="size-4 text-primary select-none" />}
        titleClassName="text-primary"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
        footer={
          <button
            onClick={() => { toast.dismiss(id); onAction(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          >
            {actionLabel}
          </button>
        }
      />
    ),
    // No timeout: the user has to act on this one, and the shell's own close
    // button is what dismisses it.
    { duration: Infinity, position: "bottom-right" }
  );
}

export function showLoadingToast(
  message: string = "Henter filer fra SharePoint/OneDrive...",
  title: string = "Henter filer"
): string | number {
  return toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-primary/55 ring-1 ring-primary/30"
        iconBgClass="bg-primary/20"
        icon={<Loader2 className="size-4 text-primary select-none animate-spin" />}
        titleClassName="text-primary"
        title={title}
        bodyClassName="text-foreground/90"
        body={message}
      />
    ),
    { duration: Infinity, position: "bottom-right" }
  );
}

export function showProgressToast(
  message: string,
  title: string = "Synkroniserer...",
  progress?: { current: number; total: number }
): string | number {
  const progressText = progress ? ` (${progress.current}/${progress.total})` : "";
  const progressPercent = progress
    ? Math.round((progress.current / progress.total) * 100)
    : undefined;

  return toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-primary/55 ring-1 ring-primary/30"
        iconBgClass="bg-primary/20"
        icon={<Loader2 className="size-4 text-primary select-none animate-spin" />}
        titleClassName="text-primary"
        title={`${title}${progressText}`}
        bodyClassName="text-foreground/90"
        body={
          <>
            <span>{message}</span>
            {progressPercent !== undefined && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            )}
          </>
        }
      />
    ),
    { duration: Infinity, position: "bottom-right" }
  );
}

export function updateProgressToast(
  toastId: string | number,
  message: string,
  title?: string,
  progress?: { current: number; total: number }
): void {
  const progressText = progress ? ` (${progress.current}/${progress.total})` : "";
  const progressPercent = progress
    ? Math.round((progress.current / progress.total) * 100)
    : undefined;

  toast.custom(
    (id) => (
      <ToastShell
        id={id}
        colorClass="border-primary/55 ring-1 ring-primary/30"
        iconBgClass="bg-primary/20"
        icon={<Loader2 className="size-4 text-primary select-none animate-spin" />}
        titleClassName="text-primary"
        title={`${title ?? "Synkroniserer..."}${progressText}`}
        bodyClassName="text-foreground/90"
        body={
          <>
            <span>{message}</span>
            {progressPercent !== undefined && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            )}
          </>
        }
      />
    ),
    { id: toastId, duration: Infinity, position: "bottom-right" }
  );
}

/** Dismiss a toast created by one of the helpers above (e.g. a progress toast). */
export function dismissToast(toastId: string | number): void {
  toast.dismiss(toastId);
}
